// ===== Vitals & Payments Page (Backend Integrated) =====
import React from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { CurrencyDollarIcon, ChartBarIcon, ArrowTrendingUpIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const VitalsPaymentsPage = () => {
  const { canAccess, venue, vitals, redemptions, loading, refreshData } = useVenueDashboard();

  if (!canAccess(['MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Vitals" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Manager role or higher required.</p>
        </div>
      </div>
    );
  }

  // Calculate stats from real data
  const thisMonthRedemptions = redemptions?.filter(r => {
    const now = new Date();
    const redemptionDate = new Date(r.createdAt);
    return redemptionDate.getMonth() === now.getMonth() && 
           redemptionDate.getFullYear() === now.getFullYear();
  }).length || 0;

  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / (1000 * 60));
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} mins ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Vitals & Payments" />
      
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Business Vitals
          </h2>
          <button
            onClick={refreshData}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              color: '#6b7280',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
            Refresh
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {!loading && (
          <>
            {/* Revenue Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    backgroundColor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CurrencyDollarIcon style={{ width: '24px', height: '24px', color: '#16a34a' }} />
                  </div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>This Month</span>
                </div>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                  {vitals?.monthlyRevenue || vitals?.revenue || '฿0'}
                </p>
                <p style={{ fontSize: '13px', color: '#10b981', margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ArrowTrendingUpIcon style={{ width: '14px', height: '14px' }} />
                  {vitals?.revenueGrowth || '+0%'} from last month
                </p>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ChartBarIcon style={{ width: '24px', height: '24px', color: '#2563eb' }} />
                  </div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Redemptions</span>
                </div>
                <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                  {thisMonthRedemptions}
                </p>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                  This month
                </p>
              </div>
            </div>

            {/* Venue Info */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                Venue Information
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <InfoRow 
                  label="Venue ID" 
                  value={(venue?._id || venue?.id || 'N/A').slice(-12)} 
                />
                <InfoRow 
                  label="Name" 
                  value={venue?.name || 'N/A'}
                />
                <InfoRow 
                  label="Category" 
                  value={venue?.category || venue?.venuecategorydisplayname || 'N/A'} 
                />
                <InfoRow 
                  label="Location" 
                  value={venue?.city || 'N/A'}
                />
                <InfoRow 
                  label="Status" 
                  value={venue?.status || 'Active'} 
                  badge 
                />
              </div>
            </div>

            {/* Payment Methods */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                Payment Methods
              </h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <PaymentBadge icon="💳" label="Card" active={vitals?.acceptsCard !== false} />
                <PaymentBadge icon="💵" label="Cash" active={vitals?.acceptsCash !== false} />
                <PaymentBadge icon="📱" label="PromptPay" active={vitals?.acceptsPromptPay} />
                <PaymentBadge icon="₿" label="Crypto" active={vitals?.acceptsCrypto} />
              </div>
            </div>

            {/* Recent Activity */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                  Recent Activity
                </h3>
              </div>
              
              {(!redemptions || redemptions.length === 0) ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <p style={{ color: '#6b7280', margin: 0 }}>No recent activity</p>
                </div>
              ) : (
                redemptions.slice(0, 5).map((redemption, index) => (
                  <div
                    key={redemption._id || redemption.id || index}
                    style={{
                      padding: '16px 24px',
                      borderBottom: index < Math.min(redemptions.length, 5) - 1 ? '1px solid #e5e7eb' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <p style={{ fontWeight: '500', color: '#1f2937', margin: 0 }}>
                        {redemption.offer?.title || redemption.offerName || 'Redemption'}
                      </p>
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: '2px 0 0 0' }}>
                        {redemption.user?.name || 'Customer'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: '500',
                        backgroundColor: '#dcfce7',
                        color: '#166534'
                      }}>
                        Redeemed
                      </span>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                        {formatTime(redemption.createdAt)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, badge }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '14px', color: '#6b7280' }}>{label}</span>
    {badge ? (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: '#dcfce7',
        color: '#166534'
      }}>
        {value}
      </span>
    ) : (
      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{value}</span>
    )}
  </div>
);

const PaymentBadge = ({ icon, label, active }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '10px',
    backgroundColor: active ? '#f0fdf4' : '#f3f4f6',
    border: `1px solid ${active ? '#86efac' : '#e5e7eb'}`
  }}>
    <span style={{ fontSize: '18px' }}>{icon}</span>
    <span style={{ 
      fontSize: '13px', 
      fontWeight: '500', 
      color: active ? '#166534' : '#9ca3af' 
    }}>
      {label}
    </span>
  </div>
);

export default VitalsPaymentsPage;
